import { Text } from '@zillow/constellation-10';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { cx } from '@/styled-system/css';
import { token } from '@/styled-system/tokens';
import { MapPin } from 'lucide-react';
import { LucideIcon } from '@/src/components/LucideIcon';
import { MapOverlay } from '@/src/components/features/map/MapOverlay';
import type { PanelContentProps } from '@/src/components/ui/PanelSystem';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { panelBody, PanelHeader } from '@/src/components/ui/PanelSystem';
import { PoiTypeCombobox } from '../shared/PoiTypeCombobox';
import { PoiInfoCard } from '../shared/PoiInfoCard';
import { CommuteAutocomplete } from '@/src/components/features/commute/CommuteAutocomplete';
import { CommuteTimeCards } from '../shared/CommuteTimeCards';
import { getPOIDetails, searchPOI } from '../shared/googlePlaces';
import { calculateCommuteTimes } from '../shared/routeCalculation';
import { useGoogleMapsLoaded, useGoogleMapsLoadFailed } from '@/src/lib/hooks';
import { useDarkMapStyles } from '@/src/lib/hooks/map/useDarkMapStyles';
import { createPOIMarkerIcon } from '../shared/poiMarkerIcons';
import { usePersistentPoiSelection } from '../shared/usePersistentPoiSelection';
import { CommuteDistanceConfirmationModal, SavedCommuteDistancePrompt } from '../shared/CommuteDistanceConfirmation';
import { getCommuteDestinationKey, getCommuteDistanceStatus } from '../shared/commuteDistance';
import { useCommutePreferences, useSetCommutePreferences } from '@/src/lib/hooks/neighborhood';
import { ShowMoreContent } from '@/src/components/features/property/ui/ShowMoreContent';
import type { CommuteDestination, CommuteTime, POIOptionValue, POIPlace, TravelMode } from '../shared/types';
import {
  addressDisplay,
  commuteLabel,
  commuteSection,
  mapContainer,
  mapPlaceholder,
  sectionContainer,
} from './neighborhoodSpoke.styles';

// Google Maps marker icons need resolved color strings; CSS var tokens are not applied inside Maps symbols.
const PROPERTY_MARKER_FILL_COLOR = '#c93a1a';
const PROPERTY_MARKER_STROKE_COLOR = '#ffffff';

type ConfirmableCommuteDestination = {
  destination: CommuteDestination;
  distanceMiles: number;
};

type ConfirmedCommuteDestination = {
  originKey: string;
  destinationKey: string;
};

function isValidCoordinate(lat: number | undefined, lon: number | undefined): boolean {
  if (lat == null || lon == null) return false;
  if (lat === 0 && lon === 0) return false;
  return lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180;
}

type NeighborhoodSpokeContentProps = PanelContentProps & {
  showHeader?: boolean;
  bodyClassName?: string;
  collapseDetails?: boolean;
  /** Pass false to defer the Google Maps script load (the map area keeps its 300px placeholder). */
  mapLoadEnabled?: boolean;
};

export function NeighborhoodSpokeContent({
  panelProps,
  showHeader = true,
  bodyClassName,
  collapseDetails = false,
  mapLoadEnabled = true,
}: NeighborhoodSpokeContentProps) {
  const t = useTranslations('hdp');
  const listing = panelProps?.listing as PropertyDetailListing | undefined;
  const address = listing?.address;
  const geo = listing?.geo;
  const mapsLoaded = useGoogleMapsLoaded(mapLoadEnabled);
  const mapsFailed = useGoogleMapsLoadFailed();

  const fullAddress = address
    ? [address.street, address.city, address.state, address.zip].filter(Boolean).join(', ')
    : null;

  const lat = geo?.lat;
  const lon = geo?.lon;
  const hasValidCoords = isValidCoordinate(lat, lon);
  const commuteOriginKey = hasValidCoords ? `${lat},${lon}` : null;

  // Map state
  const mapDivRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);
  const mapStyles = useDarkMapStyles();

  // POI state
  const [selectedPoiOptions, setSelectedPoiOptions] = usePersistentPoiSelection();
  const [selectedPoi, setSelectedPoi] = useState<POIPlace | null>(null);
  const poiMarkersRef = useRef<Map<POIOptionValue, Array<google.maps.Marker>>>(new Map());
  const poiDetailsCacheRef = useRef<Map<string, POIPlace>>(new Map());
  const selectedPoiOptionsRef = useRef(selectedPoiOptions);

  // Commute persistence
  const commutePrefs = useCommutePreferences();
  const setCommutePrefs = useSetCommutePreferences();

  // Commute state — initialized from persisted preferences
  const [destination, setDestination] = useState<CommuteDestination | null>(null);
  const [savedDestinationPrompt, setSavedDestinationPrompt] = useState<ConfirmableCommuteDestination | null>(null);
  const [pendingDestination, setPendingDestination] = useState<ConfirmableCommuteDestination | null>(null);
  const [commuteTimes, setCommuteTimes] = useState<Array<CommuteTime>>([]);
  const [selectedMode, setSelectedMode] = useState<TravelMode | undefined>(undefined);
  const [isCalculating, setIsCalculating] = useState(false);
  const [autocompleteResetKey, setAutocompleteResetKey] = useState(0);
  const routeRendererRef = useRef<google.maps.DirectionsRenderer | null>(null);
  const confirmedCommuteRef = useRef<ConfirmedCommuteDestination | null>(null);

  const clearCommuteRoute = useCallback(() => {
    routeRendererRef.current?.setMap(null);
    routeRendererRef.current = null;
  }, []);

  // Initialize Google Map
  useEffect(() => {
    if (!mapDivRef.current || !hasValidCoords || !mapsLoaded) return;
    if (typeof google === 'undefined' || typeof google.maps?.Map !== 'function') return;

    const map = new google.maps.Map(mapDivRef.current, {
      center: { lat: lat!, lng: lon! },
      zoom: 15,
      disableDefaultUI: true,
      zoomControl: true,
      clickableIcons: false,
      gestureHandling: 'cooperative',
    });

    new google.maps.Marker({
      position: { lat: lat!, lng: lon! },
      map,
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 8,

        fillColor: PROPERTY_MARKER_FILL_COLOR,
        fillOpacity: 1,

        strokeColor: PROPERTY_MARKER_STROKE_COLOR,
        strokeWeight: 2,
      },
    });

    mapInstanceRef.current = map;
    setMapInstance(map);

    const mapClickListener = map.addListener('click', () => setSelectedPoi(null));

    const currentPoiMarkers = poiMarkersRef.current;
    const currentPoiDetailsCache = poiDetailsCacheRef.current;
    const currentRouteRenderer = routeRendererRef.current;
    return () => {
      mapClickListener.remove();
      currentPoiMarkers.forEach((markers) => markers.forEach((m) => m.setMap(null)));
      currentPoiMarkers.clear();
      currentPoiDetailsCache.clear();
      mapInstanceRef.current = null;
      setSelectedPoi(null);
      setMapInstance(null);
      currentRouteRenderer?.setMap(null);
    };
  }, [hasValidCoords, lat, lon, mapsLoaded]);

  useEffect(() => {
    mapInstance?.setOptions({ styles: mapStyles });
  }, [mapInstance, mapStyles]);

  useEffect(() => {
    const savedDestination = commutePrefs.destination;
    if (!savedDestination || !hasValidCoords || !commuteOriginKey) {
      confirmedCommuteRef.current = null;
      setSavedDestinationPrompt(null);
      setPendingDestination(null);
      setDestination(null);
      setCommuteTimes([]);
      setSelectedMode(undefined);
      setIsCalculating(false);
      clearCommuteRoute();
      return;
    }

    const status = getCommuteDistanceStatus({ lat: lat!, lng: lon! }, savedDestination);
    const savedDestinationKey = getCommuteDestinationKey(savedDestination);
    const isConfirmedForCurrentOrigin =
      confirmedCommuteRef.current?.originKey === commuteOriginKey &&
      confirmedCommuteRef.current.destinationKey === savedDestinationKey;

    if (!status.isWithinAutoLimit && !isConfirmedForCurrentOrigin) {
      setSavedDestinationPrompt({ destination: savedDestination, distanceMiles: status.distanceMiles });
      setDestination(null);
      setCommuteTimes([]);
      setSelectedMode(undefined);
      setIsCalculating(false);
      clearCommuteRoute();
      return;
    }

    setSavedDestinationPrompt(null);
    setDestination(savedDestination);
    setSelectedMode(commutePrefs.travelMode);
  }, [
    clearCommuteRoute,
    commuteOriginKey,
    commutePrefs.destination,
    commutePrefs.travelMode,
    hasValidCoords,
    lat,
    lon,
  ]);

  useEffect(() => {
    if (!destination || !hasValidCoords || !commuteOriginKey) return;
    const destinationKey = getCommuteDestinationKey(destination);
    const status = getCommuteDistanceStatus({ lat: lat!, lng: lon! }, destination);
    const isConfirmedForCurrentOrigin =
      confirmedCommuteRef.current?.originKey === commuteOriginKey &&
      confirmedCommuteRef.current.destinationKey === destinationKey;
    if (!status.isWithinAutoLimit && !isConfirmedForCurrentOrigin) return;

    let cancelled = false;
    setCommuteTimes([]);
    setIsCalculating(true);
    void calculateCommuteTimes({ lat: lat!, lng: lon! }, destination)
      .then((times) => {
        if (cancelled) return [];
        setCommuteTimes(times);
        return times;
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setIsCalculating(false);
      });

    return () => {
      cancelled = true;
    };
  }, [commuteOriginKey, destination, hasValidCoords, lat, lon]);

  useEffect(() => {
    selectedPoiOptionsRef.current = selectedPoiOptions;
  }, [selectedPoiOptions]);

  const prevCoordsRef = useRef(`${lat},${lon}`);
  useEffect(() => {
    const key = `${lat},${lon}`;
    if (key !== prevCoordsRef.current) {
      prevCoordsRef.current = key;
      poiMarkersRef.current.forEach((markers) => markers.forEach((m) => m.setMap(null)));
      poiMarkersRef.current.clear();
      poiDetailsCacheRef.current.clear();
      setSelectedPoi(null);
    }
  }, [lat, lon]);

  const removePOIMarkers = useCallback((optionValue: POIOptionValue) => {
    const markers = poiMarkersRef.current.get(optionValue);
    markers?.forEach((m) => m.setMap(null));
    poiMarkersRef.current.delete(optionValue);
    setSelectedPoi((prev) => (prev?.category === optionValue ? null : prev));
  }, []);

  const handlePOISelect = useCallback((place: POIPlace) => {
    const map = mapInstanceRef.current;
    if (!map) return;

    const cacheKey = place.placeId || `${place.name}-${place.lat}-${place.lng}`;
    const cachedPlace = poiDetailsCacheRef.current.get(cacheKey);
    setSelectedPoi(cachedPlace ?? place);

    if (cachedPlace || !place.placeId) return;

    void getPOIDetails(map, place)
      .then((detailedPlace) => {
        poiDetailsCacheRef.current.set(cacheKey, detailedPlace);
        setSelectedPoi((currentPlace) => {
          const currentKey =
            currentPlace && (currentPlace.placeId || `${currentPlace.name}-${currentPlace.lat}-${currentPlace.lng}`);
          return currentKey === cacheKey ? detailedPlace : currentPlace;
        });
        return detailedPlace;
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    const map = mapInstance;
    if (!map || !hasValidCoords) return;

    const selectedSet = new Set(selectedPoiOptions);

    for (const optionValue of Array.from(poiMarkersRef.current.keys())) {
      if (!selectedSet.has(optionValue)) {
        removePOIMarkers(optionValue);
      }
    }

    for (const optionValue of selectedPoiOptions) {
      if (poiMarkersRef.current.has(optionValue)) continue;

      poiMarkersRef.current.set(optionValue, []);
      searchPOI(map, lat!, lon!, optionValue)
        .then((places: Array<POIPlace>) => {
          const markers = places.map((place) => {
            const marker = new google.maps.Marker({
              position: { lat: place.lat, lng: place.lng },
              map,
              icon: createPOIMarkerIcon(optionValue),
              title: place.name,
            });
            marker.addListener('click', () => handlePOISelect(place));
            return marker;
          });

          if (!selectedPoiOptionsRef.current.includes(optionValue)) {
            markers.forEach((marker) => marker.setMap(null));
            poiMarkersRef.current.delete(optionValue);
            return [];
          }

          poiMarkersRef.current.set(optionValue, markers);
          return markers;
        })
        .catch(() => {
          if (!selectedPoiOptionsRef.current.includes(optionValue)) {
            poiMarkersRef.current.delete(optionValue);
          }
        });
    }
  }, [hasValidCoords, lat, lon, mapInstance, selectedPoiOptions, removePOIMarkers, handlePOISelect]);

  const activateDestination = useCallback(
    (dest: CommuteDestination, travelMode: TravelMode, confirmedOverLimit = false) => {
      if (!commuteOriginKey) return;
      confirmedCommuteRef.current = confirmedOverLimit
        ? { originKey: commuteOriginKey, destinationKey: getCommuteDestinationKey(dest) }
        : null;
      setDestination(dest);
      setCommuteTimes([]);
      setSelectedMode(travelMode);
      setSavedDestinationPrompt(null);
      setPendingDestination(null);
      clearCommuteRoute();
      setCommutePrefs({ destination: dest, travelMode });
    },
    [clearCommuteRoute, commuteOriginKey, setCommutePrefs],
  );

  const resetCommuteDestination = useCallback(() => {
    confirmedCommuteRef.current = null;
    setDestination(null);
    setSavedDestinationPrompt(null);
    setPendingDestination(null);
    setCommuteTimes([]);
    setSelectedMode(undefined);
    setIsCalculating(false);
    clearCommuteRoute();
    setCommutePrefs({ destination: null, travelMode: 'DRIVING' });
    setAutocompleteResetKey((key) => key + 1);
  }, [clearCommuteRoute, setCommutePrefs]);

  const handleDestinationSelect = useCallback(
    (dest: CommuteDestination) => {
      if (!hasValidCoords) return;
      const status = getCommuteDistanceStatus({ lat: lat!, lng: lon! }, dest);
      if (!status.isWithinAutoLimit) {
        setPendingDestination({ destination: dest, distanceMiles: status.distanceMiles });
        return;
      }

      activateDestination(dest, 'DRIVING');
    },
    [activateDestination, hasValidCoords, lat, lon],
  );

  const handleModeSelect = useCallback(
    (mode: TravelMode) => {
      setSelectedMode(mode);
      if (destination) {
        setCommutePrefs({ destination, travelMode: mode });
      }
    },
    [destination, setCommutePrefs],
  );

  // Draw route on map whenever selectedMode or destination changes
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !destination || !selectedMode || !hasValidCoords) return;
    const destinationKey = getCommuteDestinationKey(destination);
    const status = getCommuteDistanceStatus({ lat: lat!, lng: lon! }, destination);
    const isConfirmedForCurrentOrigin =
      confirmedCommuteRef.current?.originKey === commuteOriginKey &&
      confirmedCommuteRef.current.destinationKey === destinationKey;
    if (!status.isWithinAutoLimit && !isConfirmedForCurrentOrigin) return;

    routeRendererRef.current?.setMap(null);
    routeRendererRef.current = null;

    const directionsService = new google.maps.DirectionsService();
    const renderer = new google.maps.DirectionsRenderer({
      map,
      suppressMarkers: true,
      preserveViewport: true,
    });
    routeRendererRef.current = renderer;

    const travelModeMap: Record<TravelMode, google.maps.TravelMode> = {
      DRIVING: google.maps.TravelMode.DRIVING,
      WALKING: google.maps.TravelMode.WALKING,
      TRANSIT: google.maps.TravelMode.TRANSIT,
      BICYCLING: google.maps.TravelMode.BICYCLING,
    };

    let cancelled = false;

    directionsService.route(
      {
        origin: { lat: lat!, lng: lon! },
        destination: { lat: destination.lat, lng: destination.lng },
        travelMode: travelModeMap[selectedMode],
        ...(selectedMode === 'TRANSIT' ? { transitOptions: { departureTime: new Date() } } : {}),
      },
      (result, status) => {
        if (cancelled) return;
        if (status === google.maps.DirectionsStatus.OK && result) {
          renderer.setDirections(result);

          const bounds = new google.maps.LatLngBounds();
          bounds.extend({ lat: lat!, lng: lon! });
          bounds.extend({ lat: destination.lat, lng: destination.lng });
          map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
        }
      },
    );

    return () => {
      cancelled = true;
      renderer.setMap(null);
    };
  }, [commuteOriginKey, selectedMode, destination, hasValidCoords, lat, lon]);

  const handleEdit = useCallback(() => {
    const map = mapInstanceRef.current;
    resetCommuteDestination();

    if (map && hasValidCoords) {
      map.setCenter({ lat: lat!, lng: lon! });
      map.setZoom(15);
    }
  }, [hasValidCoords, lat, lon, resetCommuteDestination]);

  const handleUseSavedDestination = useCallback(() => {
    if (!savedDestinationPrompt) return;
    activateDestination(savedDestinationPrompt.destination, commutePrefs.travelMode, true);
  }, [activateDestination, commutePrefs.travelMode, savedDestinationPrompt]);

  const handleUsePendingDestination = useCallback(() => {
    if (!pendingDestination) return;
    activateDestination(pendingDestination.destination, 'DRIVING', true);
  }, [activateDestination, pendingDestination]);

  const handleChooseAnotherDestination = useCallback(() => {
    setPendingDestination(null);
    setAutocompleteResetKey((key) => key + 1);
  }, []);

  const commuteDetails = (
    <div className={commuteSection}>
      {destination ? (
        <CommuteTimeCards
          destination={destination}
          commuteTimes={commuteTimes}
          isLoading={isCalculating}
          selectedMode={selectedMode}
          onModeSelect={handleModeSelect}
          onEdit={handleEdit}
        />
      ) : savedDestinationPrompt ? (
        <SavedCommuteDistancePrompt
          destination={savedDestinationPrompt.destination}
          distanceMiles={savedDestinationPrompt.distanceMiles}
          onUseAnyway={handleUseSavedDestination}
          onChangeDestination={resetCommuteDestination}
        />
      ) : (
        <div>
          <Text textStyle="body-bold" className={commuteLabel}>
            {t('calculateCommute')}
          </Text>
          <CommuteAutocomplete key={autocompleteResetKey} onDestinationSelect={handleDestinationSelect} />
        </div>
      )}
    </div>
  );

  return (
    <>
      {showHeader && <PanelHeader title={t('neighborhoodTitle')} />}
      <div className={cx(panelBody, bodyClassName)}>
        <div className={sectionContainer}>
          {fullAddress && <p className={addressDisplay}>{fullAddress}</p>}

          {hasValidCoords && mapsLoaded ? (
            <div className={mapContainer}>
              <div ref={mapDivRef} style={{ width: '100%', height: '100%' }} />
              {selectedPoi && mapInstance && (
                <MapOverlay map={mapInstance} position={{ lat: selectedPoi.lat, lng: selectedPoi.lng }}>
                  <PoiInfoCard place={selectedPoi} onClose={() => setSelectedPoi(null)} />
                </MapOverlay>
              )}
            </div>
          ) : hasValidCoords && !mapsFailed ? (
            <div className={mapPlaceholder} aria-busy="true">
              <Text textStyle="body-sm" color="text.subtle">
                {t('loadingMap')}
              </Text>
            </div>
          ) : (
            <div className={mapPlaceholder}>
              <LucideIcon icon={MapPin} size="xl" color={token.var('colors.text.subtle')} />
              <Text textStyle="body-sm" color="text.subtle">
                {t('mapNotAvailable')}
              </Text>
            </div>
          )}

          <PoiTypeCombobox selectedValues={selectedPoiOptions} onChange={setSelectedPoiOptions} />
          {collapseDetails ? (
            <ShowMoreContent
              controlsId="neighborhood-module-commute"
              buttonTestId="button-show-more-the-neighborhood"
              showMoreLabel={t('showMore')}
              showLessLabel={t('showLess')}
              showMoreAriaLabel={`${t('showMore')}: ${t('neighborhoodTitle')}`}
              showLessAriaLabel={`${t('showLess')}: ${t('neighborhoodTitle')}`}
            >
              {commuteDetails}
            </ShowMoreContent>
          ) : (
            commuteDetails
          )}
        </div>
      </div>
      <CommuteDistanceConfirmationModal
        destination={pendingDestination?.destination ?? null}
        distanceMiles={pendingDestination?.distanceMiles ?? null}
        isOpen={pendingDestination !== null}
        onUseAnyway={handleUsePendingDestination}
        onChooseAnother={handleChooseAnotherDestination}
      />
    </>
  );
}
